import { FC, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { DatePicker } from "../../../common/components/DatePicker/DatePicker";
import { List } from "../../../common/components/List/List";
import { Tabs } from "../../../common/components/Tabs/Tabs";
import {
  setActiveAppealId,
  setAppealsDateFrom,
  setAppealsDateTo,
  setAppealStatus,
} from "./desktopSlice";
import { useGetAppealStatusesQuery } from "../../../services/appealApi";
import { useGetSupportTicketsQuery } from "../../../services/supportApi";
import { SupportTicketList } from "../support/SupportTicketList";
import { Button } from "../../../common/components/Button/Button";
import { QueryLayout } from "../../../common/components/Layout/QueryLayout";
import { NotFoundTitle } from "../../../common/components/Title/NotFoundTitle";
import { getQueryDateParam } from "common/utils/helpers";

// Статусы справочника /ticket_statuses: 1 — активно, 2 — закрыто
const TICKET_STATUS_FINISHED = 2;

// Сколько обращений загружается сразу и на сколько растёт список по «Показать ещё»
const TICKETS_PAGE_SIZE = 50;

export const AppealsTabs: FC = () => {
  const [perPage, setPerPage] = useState(TICKETS_PAGE_SIZE);

  const appealDateFrom = useAppSelector(
    (state) => state.platformManager.desktop.appealsDateFrom
  );
  const appealsDateTo = useAppSelector(
    (state) => state.platformManager.desktop.appealsDateTo
  );
  const appealsStatusId = useAppSelector(
    (state) => state.platformManager.desktop.appealStatus
  );
  const activeAppealId = useAppSelector(
    (state) => state.platformManager.desktop.activeAppealId
  );

  const {
    data,
    isLoading: isAppealsLoading,
    isFetching: isAppealsFetching,
    error: appealsError,
  } = useGetSupportTicketsQuery(
    {
      status:
        appealsStatusId === TICKET_STATUS_FINISHED ? "finished" : "active",
      dateFrom: getQueryDateParam(appealDateFrom),
      dateTo: getQueryDateParam(appealsDateTo),
      perPage,
    },
    // Страховка на случай разрыва сокета: список обновляется и сам
    { pollingInterval: 60000 }
  );
  const appeals = data?.tickets;

  const { data: appealStatuses } = useGetAppealStatusesQuery();

  const dispatch = useAppDispatch();
  const appealStatus = useAppSelector(
    (state) => state.platformManager.desktop.appealStatus
  );

  const handleSetActiveAppeal = (id: number) => {
    dispatch(setActiveAppealId(id));
  };

  const handleSetAppealsStatus = (status: number) => {
    setPerPage(TICKETS_PAGE_SIZE);
    dispatch(setAppealStatus(status));
  };

  const handleAppealsDateFromChange = (date: Nullable<Date>) => {
    dispatch(setAppealsDateFrom(date?.toLocaleDateString("sv")));
  };

  const handleAppealsDateToChange = (date: Nullable<Date>) => {
    dispatch(setAppealsDateTo(date?.toLocaleDateString("sv")));
  };

  return (
    <>
      {appealStatuses && (
        <Tabs
          tabs={appealStatuses.map((status) => ({
            value: status.id,
            label: status.name,
          }))}
          value={appealStatus}
          onTabClick={handleSetAppealsStatus}
        />
      )}
      <div>
        <DatePicker
          onStartDateChange={handleAppealsDateFromChange}
          onEndDateChange={handleAppealsDateToChange}
          placeholderText="Введите промежуток дат"
          isClearable={true}
        />
      </div>

      <QueryLayout
        isLoading={isAppealsLoading}
        // Список перечитывается по сокету и таймеру: спиннер только на первой загрузке
        isFetching={isAppealsFetching && !appeals}
        error={appealsError}
      >
        {appeals?.length ? (
          <List className="mt-[10px] overflow-y-scroll">
            <SupportTicketList
              tickets={appeals}
              activeTicketId={activeAppealId}
              onItemClick={handleSetActiveAppeal}
            />
            {data && data.total > appeals.length && (
              <Button
                mode="darkBorder"
                className="mb-[15px]"
                onClick={() => setPerPage((current) => current + TICKETS_PAGE_SIZE)}
              >
                Показать ещё
              </Button>
            )}
          </List>
        ) : (
          <NotFoundTitle />
        )}
      </QueryLayout>
    </>
  );
};
